import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageHero } from "@/components/site/PageHero";
import { Frame } from "@/components/site/Frame";
import { CTA } from "@/components/site/CTA";
import { PROJECTS } from "@/data/content";

const FILTERS = ["All", "Architecture", "Extensions", "Lofts", "Refurbishment", "Interiors"];

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const list = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <PageHero
        testId="projects-hero"
        eyebrow="Projects"
        lines={["Work that", "fills the box."]}
        intro="A selection of the kinds of spaces we design and build. Imagery shown is illustrative while our project portfolio is being photographed."
      />

      <section className="mx-auto max-w-[1440px] px-5 sm:px-10 pb-28 sm:pb-36">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="project-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`project-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`font-mono text-xs uppercase tracking-[0.18em] px-5 py-3 border transition-colors ${
                filter === f ? "bg-[#d96b27] border-[#d96b27] text-[#0c0e12]" : "border-[#262b38] text-[#a7aebb] hover:text-[#f4f5f7] hover:border-[#a7aebb]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="grid lg:grid-cols-12 gap-6">
          {list.map((p) => (
            <motion.button
              layout
              key={p.id}
              data-testid={`project-card-${p.id}`}
              onClick={() => setActive(p)}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className={`text-left ${filter === "All" ? p.span : "lg:col-span-6"}`}
            >
              <Frame src={p.img} alt={p.title} className="aspect-[4/3] lg:aspect-auto lg:h-[420px]" parallax={false} />
              <div className="flex justify-between items-baseline mt-4 gap-4">
                <h3 className="font-display text-xl sm:text-2xl font-bold">{p.title}</h3>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb] shrink-0">{p.category}</span>
              </div>
            </motion.button>
          ))}
        </motion.div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="project-modal" className="max-w-4xl bg-[#0c0e12] border-[#262b38] p-0 overflow-hidden [&>button]:hidden">
          {active && (
            <div>
              <img src={active.img} alt={active.title} className="w-full h-[50vh] object-cover" />
              <div className="p-6 sm:p-8 flex justify-between items-start gap-6">
                <div>
                  <DialogTitle className="font-display text-3xl font-bold">{active.title}</DialogTitle>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb] mt-3">
                    {active.category} · {active.place} · Illustrative imagery
                  </p>
                </div>
                <button data-testid="project-modal-close" onClick={() => setActive(null)} className="w-11 h-11 border border-[#262b38] flex items-center justify-center hover:border-[#d96b27]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CTA />
    </div>
  );
}
